<template>
  <header
    :class="[
      'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      isScrolled ? 'bg-accent shadow-lg' : 'bg-accent',
    ]"
    role="banner"
  >
    <nav class="container-custom" aria-label="Navegación principal">
      <div class="flex items-center justify-between h-20">
        <!-- Logo -->
        <a href="#inicio" class="flex items-center" @click.prevent="scrollTo('inicio')" aria-label="MotoBoy Chile - Ir al inicio">
          <img
            src="/images/logo/mb.svg"
            alt="MotoBoy Chile - Mensajería Express"
            class="h-[100px] w-auto object-contain"
            loading="eager"
            onerror="this.onerror=null; this.src='data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22150%22 height=%2250%22%3E%3Ctext x=%2210%22 y=%2235%22 font-family=%22Arial%22 font-size=%2230%22 font-weight=%22bold%22 fill=%22%231c0f61%22%3EMotoBoy%3C/text%3E%3C/svg%3E'"
          />
        </a>

        <!-- Desktop Menu -->
        <ul class="hidden lg:flex items-center space-x-8" role="menubar">
          <li
            v-for="item in menuItems"
            :key="item.id"
            role="none"
          >
            <a
              :href="item.hash"
              class="text-navy-500 hover:text-navy-700 font-medium text-sm uppercase tracking-wide transition-colors duration-200"
              @click.prevent="handleMenuClick(item)"
              role="menuitem"
              :aria-label="$t(`nav.${item.id}`)"
            >
              {{ $t(`nav.${item.id}`) }}
            </a>
          </li>

          <!-- WhatsApp Button -->
          <li role="none">
            <a
              href="https://wa.me/56992345248"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contactar por WhatsApp al +569 9234 5248"
              class="btn-primary flex items-center gap-2"
            >
              <font-awesome-icon :icon="['fab', 'whatsapp']" class="text-xl" aria-hidden="true" />
              <span class="hidden xl:inline">+569 9234 5248</span>
            </a>
          </li>
        </ul>

        <!-- Mobile Menu Button -->
        <button
          @click="isMobileMenuOpen = !isMobileMenuOpen"
          class="lg:hidden text-navy-500 hover:text-navy-700 transition-colors"
          type="button"
          :aria-label="isMobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'"
          :aria-expanded="isMobileMenuOpen"
          aria-controls="mobile-menu"
        >
          <font-awesome-icon
            :icon="isMobileMenuOpen ? 'times' : 'bars'"
            class="text-2xl"
            aria-hidden="true"
          />
        </button>
      </div>

      <!-- Mobile Menu -->
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 -translate-y-4"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-4"
      >
        <nav
          id="mobile-menu"
          v-show="isMobileMenuOpen"
          aria-label="Navegación móvil"
          class="lg:hidden py-4 space-y-4 bg-white rounded-lg shadow-xl mt-2 border-t-4 border-primary"
        >
          <ul role="menu">
            <li
              v-for="item in menuItems"
              :key="item.id"
              role="none"
            >
              <a
                :href="item.hash"
                class="block px-4 py-2 text-navy-500 hover:bg-primary-50 hover:text-primary transition-colors font-medium"
                @click.prevent="handleMobileMenuClick(item)"
                role="menuitem"
                :aria-label="$t(`nav.${item.id}`)"
              >
                {{ $t(`nav.${item.id}`) }}
              </a>
            </li>
          </ul>

          <!-- Mobile WhatsApp Button -->
          <div class="px-4 pt-2 border-t border-gray-200">
            <a
              href="https://wa.me/56992345248"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contactar por WhatsApp al +569 9234 5248"
              class="btn-primary w-full flex items-center justify-center gap-2"
            >
              <font-awesome-icon :icon="['fab', 'whatsapp']" class="text-xl" aria-hidden="true" />
              <span>+569 9234 5248</span>
            </a>
          </div>
        </nav>
      </transition>
    </nav>
  </header>

  <!-- Spacer para compensar header fijo -->
  <div class="h-20"></div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';

interface MenuItem {
  id: string;
  sectionId?: string;
  hash: string;
  route?: string; // Para páginas separadas como tracking
}

const menuItems: MenuItem[] = [
  { id: 'home', sectionId: 'inicio', hash: '#inicio' },
  { id: 'services', sectionId: 'servicios', hash: '#servicios' },
  { id: 'about', sectionId: 'nosotros', hash: '#nosotros' },
  { id: 'contact', sectionId: 'contacto', hash: '#contacto' },
  { id: 'tracking', route: '/tracking', hash: '#tracking' }, // Página de tracking
  { id: 'news', sectionId: 'noticias', hash: '#noticias' },
];

const isScrolled = ref(false);
const isMobileMenuOpen = ref(false);

const handleScroll = () => {
  isScrolled.value = window.scrollY > 20;
};

const handleMenuClick = (item: MenuItem) => {
  // Si tiene route, navegar a la página
  if (item.route) {
    window.location.href = item.route;
    return;
  }

  // Si tiene sectionId, hacer scroll a la sección
  if (item.sectionId) {
    // Verificar si estamos en la página home
    const isHomePage = window.location.pathname === '/';

    if (!isHomePage) {
      // Si no estamos en home, navegar a home con el hash
      window.location.href = `/${item.hash}`;
      return;
    }

    // Si estamos en home, hacer scroll a la sección
    const element = document.getElementById(item.sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  }
};

const handleMobileMenuClick = (item: MenuItem) => {
  isMobileMenuOpen.value = false;
  setTimeout(() => handleMenuClick(item), 100);
};

// Mantener scrollTo para el logo (compatibilidad)
const scrollTo = (id: string) => {
  // Verificar si estamos en la página home
  const isHomePage = window.location.pathname === '/';

  if (!isHomePage) {
    // Si no estamos en home, navegar a home con el hash
    window.location.href = `/#${id}`;
    return;
  }

  // Si estamos en home, hacer scroll a la sección
  const element = document.getElementById(id);
  if (element) {
    const headerOffset = 80;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style scoped>
/* Estilos adicionales si es necesario */
</style>
